import type { Membership, MembershipRole, Session } from '@/entities/session/model/session'
import type { MyPageV4 } from '@/shared/api/generated/models/MyPageV4'
import type { MyRoleV4 } from '@/shared/api/generated/models/MyRoleV4'
import { normalizeLanguage } from '@/shared/lib/language/language'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const readString = (record: Record<string, unknown>, ...keys: string[]): string | undefined => {
  const value = keys.map((key) => record[key]).find((item) => typeof item === 'string')
  return typeof value === 'string' ? value : undefined
}

const unwrap = (payload: unknown): Record<string, unknown> => {
  if (!isRecord(payload)) {
    return {}
  }
  const result = payload.result ?? payload.Result
  return isRecord(result) ? result : payload
}

const roleByValue: Record<number, MembershipRole> = {
  1: 'user',
  2: 'manager',
  4: 'admin',
  8: 'support',
}

const parseRoles = (value: unknown): MembershipRole[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.flatMap((role) => {
    if (typeof role === 'number' && roleByValue[role]) {
      return [roleByValue[role]]
    }

    if (typeof role !== 'string') {
      return []
    }

    const normalizedRole = role.toLowerCase()
    return normalizedRole === 'user' ||
      normalizedRole === 'manager' ||
      normalizedRole === 'admin' ||
      normalizedRole === 'support'
      ? [normalizedRole]
      : []
  })
}

const parseMembership = (value: unknown, fallbackId: string): Membership | undefined => {
  if (!isRecord(value)) {
    return undefined
  }

  const organizationType = readString(value, 'organizationType', 'roleType', 'type')
  const supportedType =
    organizationType === 'placement-provider' || organizationType === 'Org'
      ? 'placement-provider'
      : organizationType === 'platform' || organizationType === 'Edy365'
        ? 'platform'
        : 'educational-institution'

  return {
    id: readString(value, 'id', 'orgId') ?? fallbackId,
    organizationName: readString(value, 'organizationName', 'name', 'title') ?? 'Edy365',
    organizationType: supportedType,
    roles: parseRoles(value.roles),
  }
}

const parsePages = (value: unknown): MyPageV4[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value.flatMap((page) => {
    if (!isRecord(page) || typeof page.type !== 'string' || page.type.length === 0) {
      return []
    }

    return [
      {
        type: page.type,
        events: typeof page.events === 'number' ? page.events : -1,
      },
    ]
  })
}

const getContextId = (context: MyRoleV4) => context.orgId ?? `${context.roleType}:${context.name}`

const getContextOrganizationType = (context: MyRoleV4): Membership['organizationType'] => {
  if (context.roleType === 'Org') {
    return 'placement-provider'
  }
  if (context.roleType === 'Edy365') {
    return 'platform'
  }
  return 'educational-institution'
}

export const adaptSession = (
  authPayload: unknown,
  mePayload?: unknown,
  roleContexts: MyRoleV4[] = []
): Session => {
  const authResult = unwrap(authPayload)
  const authUser = isRecord(authResult.user) ? authResult.user : authResult
  const meResult = unwrap(mePayload)
  const meUser = isRecord(meResult.user) ? meResult.user : meResult
  const user = { ...meUser, ...authUser }
  const userId = readString(user, 'id', 'userId') ?? 'current-user'
  const parsedActiveMembership = parseMembership(user.activeMembership, 'active-membership') ??
    parseMembership(user.organization, 'active-membership') ?? {
      id: 'active-membership',
      organizationName: readString(user, 'organizationName', 'orgName') ?? 'Edy365',
      organizationType: 'educational-institution',
      roles: parseRoles(user.roles),
    }
  const currentContext = roleContexts.find((context) => context.isCurrent)
  const currentRoles = parseRoles(user.roles)
  const activeMembership: Membership = {
    ...parsedActiveMembership,
    id: currentContext ? getContextId(currentContext) : parsedActiveMembership.id,
    organizationName: currentContext?.name ?? parsedActiveMembership.organizationName,
    organizationType: currentContext
      ? getContextOrganizationType(currentContext)
      : parsedActiveMembership.organizationType,
    roles: currentRoles.length > 0 ? currentRoles : parsedActiveMembership.roles,
  }

  const memberships = Array.isArray(user.memberships)
    ? user.memberships
        .map((membership, index) => parseMembership(membership, `membership-${index}`))
        .filter((membership): membership is Membership => Boolean(membership))
    : [activeMembership]
  const pagesSource = [authUser.pages, authResult.pages, meUser.pages, meResult.pages].find(
    Array.isArray
  )
  const pages = parsePages(pagesSource)

  return {
    user: {
      id: userId,
      displayName:
        readString(user, 'displayName', 'fullName', 'name') ??
        readString(user, 'email') ??
        'Edy365 lietotājs',
      email: readString(user, 'email') ?? '',
      hasPortrait: user.hasPortrait === true,
      language: normalizeLanguage(user.lang),
    },
    activeMembership,
    memberships: memberships.length > 0 ? memberships : [activeMembership],
    roleContexts,
    pages,
    supportSession:
      user.isSupportCase === true
        ? { caseNumber: readString(user, 'supportCaseId') ?? '' }
        : undefined,
  }
}
